// Semantic colour roles for the redesigned mobile app, one palette per theme
// mode. Values come from the approved mockups; a few were darkened or
// brightened so text pairs reach 4.5:1 (and icons 3:1) on their surfaces:
// light success, faint and muted icon; midnight faint and muted icon; light
// to-do row border (stronger so done and to-do rows stay distinct in sunlight).
//
// Roles (same keys in every palette):
// - canvas: screen background. surface: header, tab bar, action dock, sheets.
// - card: grouped cards, done rows, inputs, secondary buttons, tiles.
// - raised + raisedBorder: actionable to-do rows and list rows.
// - border: hairline dividers and progress tracks. borderStrong: control outlines.
// - text, textSecondary, textFaint (footnotes), subheading (position lines).
// - icon (header and quick-action icons), iconMuted (to-do circles, chevrons).
// - accent (links, back, active tab), accentPressed, onChip (text on chip/tag).
// - primary + onPrimary: primary buttons. progress: progress fill.
// - current, currentBorder, currentSubtle: the step or stop to do now.
// - chip (stop tags, info notes, tonal buttons), tag (small run-list tags).
// - success*, warning*, danger*: status colours and their backgrounds.
// - disabled*, blocked*: unavailable buttons and locked steps. overlay: modal backdrop.
// - amberSolid + onAmberSolid: Healthy Choices Amber rating circle; a clear amber with dark
//   text in every theme (the warning text colour reads brown in Light).
// - qrBackground: behind scannable QR codes (Shift pass); white in every theme
//   so kiosks read the black-on-white code.

const OVERLAY = 'rgba(5, 8, 15, 0.62)';
const QR_BACKGROUND = '#ffffff';
const AMBER_SOLID = '#f5b800';
const ON_AMBER_SOLID = '#1f1600';

export const darkBluePalette = {
	canvas: '#151b27',
	surface: '#1c2333',
	card: '#1d2433',
	raised: '#232b3c',
	raisedBorder: '#232b3c',
	border: '#2a3346',
	borderStrong: '#34405a',
	text: '#eef1f6',
	textSecondary: '#a7b2c4',
	textFaint: '#8d98ab',
	subheading: '#d6dce7',
	icon: '#cfd6e2',
	iconMuted: '#7d889b',
	accent: '#8fbaf0',
	accentPressed: '#b9d4f6',
	onChip: '#cfe0f7',
	primary: '#2563c9',
	onPrimary: '#ffffff',
	progress: '#4a8fe0',
	current: '#1f3150',
	currentBorder: '#4a8fe0',
	currentSubtle: '#c3d3ea',
	chip: '#26314a',
	tag: '#2c3650',
	success: '#5fd39a',
	successBg: '#1e3a2c',
	onSuccessBg: '#7fe0ae',
	successCard: '#1b3227',
	successCardBorder: '#2f5c45',
	onSuccessCard: '#b5dcc6',
	warning: '#f6c27a',
	warningBg: '#33281a',
	warningBorder: '#6e5222',
	warningTagBg: '#4a3516',
	danger: '#f08a8a',
	dangerBg: '#3a1f24',
	dangerBorder: '#7a3439',
	dangerSolid: '#b42318',
	onDangerSolid: '#ffffff',
	amberSolid: AMBER_SOLID,
	onAmberSolid: ON_AMBER_SOLID,
	disabledBg: '#2a3346',
	disabledText: '#8d98ab',
	blockedBg: '#181e2b',
	blockedBorder: '#3a4459',
	blockedText: '#b3bccb',
	overlay: OVERLAY,
	qrBackground: QR_BACKGROUND,
};

export const lightPalette = {
	canvas: '#f2f4f8',
	surface: '#ffffff',
	card: '#e7ebf1',
	raised: '#ffffff',
	raisedBorder: '#c9d1de',
	border: '#dde2ea',
	borderStrong: '#c9d1de',
	text: '#172033',
	textSecondary: '#566174',
	textFaint: '#5d6879',
	subheading: '#2c3a52',
	icon: '#3a4558',
	iconMuted: '#7b8598',
	accent: '#1d5bbf',
	accentPressed: '#174a9c',
	onChip: '#1f3b66',
	primary: '#2563c9',
	onPrimary: '#ffffff',
	progress: '#2563c9',
	current: '#e6efff',
	currentBorder: '#2563c9',
	currentSubtle: '#2f4a75',
	chip: '#e3e9f3',
	tag: '#e3e9f3',
	success: '#17703f',
	successBg: '#dff3e8',
	onSuccessBg: '#17663f',
	successCard: '#e4f5ec',
	successCardBorder: '#a8d9bd',
	onSuccessCard: '#2f6b4a',
	warning: '#8a5200',
	warningBg: '#fff4e2',
	warningBorder: '#f0c98a',
	warningTagBg: '#fbe3bd',
	danger: '#c0352b',
	dangerBg: '#fdecea',
	dangerBorder: '#f1b8b3',
	dangerSolid: '#b42318',
	onDangerSolid: '#ffffff',
	amberSolid: AMBER_SOLID,
	onAmberSolid: ON_AMBER_SOLID,
	disabledBg: '#e7ebf1',
	disabledText: '#636d80',
	blockedBg: '#eef1f5',
	blockedBorder: '#c3cad6',
	blockedText: '#5f6a7d',
	overlay: OVERLAY,
	qrBackground: QR_BACKGROUND,
};

// Shown as "Onyx"; the same near-black, blurple palette as the shared Onyx web and Chat theme.
export const midnightPurplePalette = {
	canvas: '#131416',
	surface: '#0c0c0e',
	card: '#1c1d21',
	raised: '#232428',
	raisedBorder: '#232428',
	border: '#26272b',
	borderStrong: '#35363c',
	text: '#f2f3f5',
	textSecondary: '#949ba4',
	textFaint: '#949ba4',
	subheading: '#dbdee1',
	icon: '#dbdee1',
	iconMuted: '#80848e',
	accent: '#949cf7',
	accentPressed: '#c9cdfb',
	onChip: '#dbdee1',
	primary: '#5865f2',
	onPrimary: '#ffffff',
	progress: '#5865f2',
	current: '#272a52',
	currentBorder: '#5865f2',
	currentSubtle: '#c9cdfb',
	chip: '#26272c',
	tag: '#26272c',
	success: '#4cb782',
	successBg: '#1a2e24',
	onSuccessBg: '#6fd19a',
	successCard: '#18291f',
	successCardBorder: '#2a4a37',
	onSuccessCard: '#a8d5bb',
	warning: '#f2c94c',
	warningBg: '#2e2716',
	warningBorder: '#5c4a1c',
	warningTagBg: '#3d3218',
	danger: '#eb5757',
	dangerBg: '#2e1718',
	dangerBorder: '#5c2526',
	dangerSolid: '#b42318',
	onDangerSolid: '#ffffff',
	amberSolid: AMBER_SOLID,
	onAmberSolid: ON_AMBER_SOLID,
	disabledBg: '#202125',
	disabledText: '#80848e',
	blockedBg: '#0c0c0e',
	blockedBorder: '#35363c',
	blockedText: '#a0a5ad',
	overlay: OVERLAY,
	qrBackground: QR_BACKGROUND,
};
